import { isTreeTrimIssue } from '@dte/otw-azure-functions/src/common/reports/customerReport/symptomsToCallCodeV3/treeNeedsTrimmingV3';
import { ReportQuestion } from '@dte/otw-azure-functions/src/report/questions/ReportQuestion';
import { isEmptyArray } from '@dte/otw-utils-core/src/objects/isEmpty';
import { stableId } from '@dte/otw-utils-core/src/strings/stableId';
import { atom, useAtomValue } from 'jotai';
import type { ReactNode } from 'react';
import { ErrorAlert } from '../../../../../../../components/Alerts/ErrorAlert';
import { InfoAlert } from '../../../../../../../components/Alerts/InfoAlert';
import { WarningAlert } from '../../../../../../../components/Alerts/WarningAlert';
import { selectedAnswersAtom } from '../../../_atoms/draftReport/stepData';
import { ShowIfAnswerSelected } from '../../HideFromCategory';
import {
	useEmergencyCallReasons,
	useEmergencyServicesNeedToBeCalled,
} from '../safety/emergencyResponse/useEmergencyServicesNeedToBeCalled';
import { useIsPoliceFire } from '../safety/useIsPoliceFire';
import { FrequentOutageAdvisements } from './FrequentOutageAdvisements';
import { useIsDownWire } from './useIsDownWire';
import { useProjectedCallCode } from '../../../useProjectedCallCode';

export interface AdvertisementsProps {
	callCode?: string;
}

export function FireSmokeBurningSmellAdvisement(): ReactNode {
	// Don't need to show this if it's a police/fire report
	const policeFire = useIsPoliceFire();
	if (policeFire) {
		return null;
	}

	return (
		<ShowIfAnswerSelected question={ReportQuestion.FireSmokeBurningSmell}>
			<ErrorAlert>
				If there is fire, smoke or a burning smell, leave the area now and call 911 to report the problems.
			</ErrorAlert>
		</ShowIfAnswerSelected>
	);
}

// TODO: This shows up for some hazards in CRM like fire at pole
export function PetsHazardAdvisement(): ReactNode {
	return <p>Keep everyone, including pets away from the hazard.</p>;
}

function DownWireAdvisementContent(): ReactNode {
	return (
		<p>
			Stay at least 25 feet away from a downed power line and make sure that you and your family, pets and
			neighbors don't touch anything it touches - like a fences, or a puddle.
		</p>
	);
}

export function DownWireAdvisements(): ReactNode {
	const downWire = useIsDownWire();
	if (!downWire) {
		return null;
	}

	// TODO: this should probably cover exposed underground wires too
	return (
		<ErrorAlert>
			<DownWireAdvisementContent />
		</ErrorAlert>
	);
}

const advisementsMap: Record<string, string[]> = {
	'If you are having trouble with the cable, internet, or landline phone, the down wire may not belong to DTE. Please also contact your telecom company to report the downed wire.':
		['PPWD', 'SDLTSOK'],
	'If the AMI meter responds with full voltage (power) your order may be cancelled.': [
		'LOWVOL',
		'XCURR',
		'ONELEG',
		'HIGHVOL',
	],
};

function buildWarningAdvisementContent(callCode: string): ReactNode {
	const advisements: ReactNode[] = [];

	for (const [advisement, codes] of Object.entries(advisementsMap)) {
		if (codes.includes(callCode)) {
			const key = stableId(advisement);
			advisements.push(<p key={key}>{advisement}</p>);
		}
	}

	if (isEmptyArray(advisements)) {
		return null;
	}

	return <>{advisements}</>;
}

export function WarningAdvisements(props: AdvertisementsProps): ReactNode {
	const content = buildWarningAdvisementContent(props.callCode ?? '');
	return content ? <WarningAlert>{content}</WarningAlert> : null;
}

export interface EmergencyAdvisementsOverideProps {
	reasons?: string[];
	alwaysShow?: boolean;
}

function Call911AdvisementContent(props: EmergencyAdvisementsOverideProps): ReactNode {
	const callReasons = useEmergencyCallReasons(props.reasons);
	const needToCall = useEmergencyServicesNeedToBeCalled(props.reasons, props.alwaysShow);
	if (!needToCall) {
		return null;
	}

	const reasonsListElements = callReasons.map((reason) => {
		const key = stableId(reason);
		return <li key={key}>{reason}</li>;
	});

	return (
		<>
			<p style={{ marginBottom: 0 }}>We advise calling 911 for any emergencies involving:</p>
			<ul>{reasonsListElements}</ul>
		</>
	);
}

export function Call911Advisements(props: EmergencyAdvisementsOverideProps): ReactNode {
	const needToCall = useEmergencyServicesNeedToBeCalled(props.reasons, props.alwaysShow);
	if (!needToCall) {
		return null;
	}

	return (
		<ErrorAlert>
			<Call911AdvisementContent {...props} />
		</ErrorAlert>
	);
}

export function DebrisAdvisement(): ReactNode {
	return (
		<p>
			For information on disposing of wood and other debris, please contact your community's Department of Public
			Works, private trash collector, or a tree removal service.
		</p>
	);
}

// export function TreeDebrisAdvisement(): ReactNode {
// 	const stormMode = useIsStormMode();

// 	const advisements: ReactNode[] = [
// 		<p key="bringToGround">
// 			We care about your safety, so we will remove any vegetation from your electric lines or other DTE equipment
// 			that fell due to natural causes and bring it to the ground safely.
// 		</p>,
// 		<p key="pileWood">
// 			Large pieces of wood (over 4 inches in diameter) will be cut into manageable firewood-sized lengths and
// 			piled on property.
// 		</p>,
// 	];

// 	if (stormMode === true) {
// 		advisements.push(
// 			<p key="homeownerRemovesSmallDebris">
// 				During storm: No debris, including wood 4 inches or less in diameter, will be removed from the
// 				customer's property. Removal of this debris is the responsibility of the homeowner.
// 			</p>,
// 		);
// 	} else if (stormMode === false) {
// 		advisements.push(
// 			<p key="dteRemovesSmallDebris">
// 				If we trim, we will remove any debris that we cause while trying to access your equipment, as long as it
// 				is 4 inches in diameter or less.
// 			</p>,
// 		);
// 	}
// 	advisements.push(<DebrisAdvisement key="debrisAdvisement" />);
// 	return <InfoAlert title="Debris Handling">{advisements}</InfoAlert>;
// }

export function SelfServicePromotionAdvisement(): ReactNode {
	// Don't show self-service promotion for police / fire
	const policeFire = useIsPoliceFire();
	if (policeFire) {
		return null;
	}

	return (
		<InfoAlert title="Tips">
			Did you know you can report or check the status of outages via our <b>smartphone app</b> or website at
			<b> outage.dteenergy.com</b>?
		</InfoAlert>
	);
}

const expectationAdvisementsMap: Record<string, string[]> = {
	'Your order will go to our Dispatch Center and the appropriate DTE resource will be dispatched for analysis and/or repairs.':
		['TRAPPED', 'CONTACT', 'PREFLT', 'BP', 'HOSP', 'EHS', 'HIC', 'PRIMARY', 'SCHOOL'],
	'The order will go to the dispatch center and the appropriate DTE resource will be dispatched to the location.': [
		'WDPF',
		'MISCPF',
	],
	'Your order will go to the dispatch center and the appropriate DTE resource will be dispatched for analysis and/or repairs. Full repairs may require multiple visits by our crews.':
		[
			'PPWD',
			'SDXL',
			'SDLTSOK',
			'XCURR',
			'LOWVOL',
			'FLICKER',
			'ONELEG',
			'PTFIRE',
			'HZ',
			'ARC',
			'OPL',
			'NONHZD',
			'SLO',
			'UGEXP',
			'HIGHVOL',
		],
	'Normal repair is within 5 business days. If Underground cable problems or pole replacement is needed, repair can take up to 10 business days.':
		['OPL', 'SLO'],
	'Our field tech may be required to administer services to your equipment and/or may alert you to issues requiring an electrician, at a cost of $40 during normal work hours, and $55 on weekends/holidays.':
		['LOWVOL', 'ONELEG', 'FLICKER', 'HIGHVOL'],
	"Please ensure pets are safely secured inside prior to our crew's arrival or they will not be able to enter your property to assess damage or restore power.":
		[
			'LOWVOL',
			'ONELEG',
			'UGEXP',
			'XCURR',
			'WDPF',
			'SDXL',
			'SDLTSOK',
			'PPWD',
			'OPL',
			'NONHZD',
			'HZ',
			'FLICKER',
			'BP',
			'ARC',
			'HIGHVOL',
		],
	"Will you ensure your pets are safely secured inside your home prior to our crew's arrival? If not, they will not be able to enter your property to access damage or restore power. Thank you.":
		['SCHOOL', 'PRIMARY', 'PREFLT', 'HOSP', 'HIC', 'EHS'],
	'Temporary restoration of full 110v power may be made until permanent repairs can be made (multi-arc).': ['ONELEG'],
	'240v equipment will not work until permanent repairs are complete.': ['ONELEG'],
	'Prior to beginning work, the tech will have you sign off on a receipt acknowledging the charges.': [
		'ONELEG',
		'LOWVOL',
		'FLICKER',
		'HIGHVOL',
	],
	'Please be assured we are working as quickly as possible to address this issue. However, DTE Energy does not provide estimates or status updates for this type of power problem.':
		[
			'TRAPPED',
			'CONTACT',
			'PTFIRE',
			'MISCPF',
			'STRUFIREPF',
			'TRAFCSIGPF',
			'HZ',
			'FLICKER',
			'LOWVOL',
			'HIGHVOL',
			'INTERRUPTIBLE',
			'UGEXP',
			'TT',
			'BP',
			'ARC',
			'NONHZD',
			'HIGHVOL',
		],

	'Please be assured we are working as quickly as possible to address this issue. However, DTE Energy does not provide estimates or status updates for this type of power problem. Downed Power lines are addressed as soon as possible.':
		['SDLTSOK', 'WDPF', 'PPWD'],

	'DTE will make permanent repairs, if possible, or temporary repairs and order a new pole for installation at a later date. This may require multiple visits.':
		['BP'],
	'We understand the urgency of your request and we will send an assessor out to your property within 7 days.': [
		'TT',
	],
	'Our goal is to ensure public safety and electrical reliability to all of our customers. Our assessors are trained to identify if trimming is needed to ensure this goal is met.':
		['TT'],
	'We care about your safety, so we will remove any vegetation from your electric lines or other DTE equipment that fell due to natural causes and bring it to the ground safely, but it will be your responsibility to dispose of it.':
		['TT'],
	'If we return to trim, we will remove any debris that we cause while trying to access your equipment, as long as it 4 inches in diameter or less.':
		['TT'],
	'For your convenience, large pieces of wood will be cut into manageable firewood sized lengths and piled on property.':
		['TT'],
	"During Storm: No Debris, including wood under 4 inches in diameter, will be removed from the customer's property. Removal of this debris is the responsibility of the homeowner.":
		['TT', 'NONHZD'],
	"For information on disposing of wood and other debris, please contact your community's Department of Public Works, private trash collector, a professional, or tree removal service.":
		['TT'],
	"Do you have any pets? (Y or N) If yes: Please ensure that pets are safely secured inside prior to our crew's arrival. This way our crews are able to safely enter your property to complete your request.":
		['TT'],
};

export function ExpectationAdvisements({ callCode }: { callCode?: string }): ReactNode {
	if (!callCode) {
		return null;
	}

	const advisements: ReactNode[] = [];

	for (const [advisement, codes] of Object.entries(expectationAdvisementsMap)) {
		if (codes.includes(callCode)) {
			const key = stableId(advisement);
			advisements.push(<p key={key}>{advisement}</p>);
		}
	}

	if (isEmptyArray(advisements)) {
		return null;
	}

	return <InfoAlert title="Customer Expectations">{advisements}</InfoAlert>;
}

const precautionAdvisementsMap: Record<string, string[]> = {
	'The trapped person should remain where they are (vehicle, home, elevator, etc.) until DTE arrives at the scene and removes the hazard.':
		['TRAPPED', 'CONTACT'],
	'Treat all wires as live, stay at least 25 feet away from a downed wire or anything it may be touching, including water.':
		['TRAPPED', 'CONTACT', 'PPWD', 'SDXL', 'SDLTSOK'],
	'Treat all wires as live. Stay at least 25 feet away from any exposed wire or anything it may be touching, including water.':
		['UGEXP'],
	'Do not drive across a downed wire or attempt to move it.': ['TRAPPED', 'CONTACT', 'PPWD', 'SDXL', 'SDLTSOK'],
	'If there is a fire and the fire department has not been contacted, advise caller to contact the fire department.':
		['PPWD', 'SDXL', 'SDLTSOK'],
	'If out of power: To prevent damage that may occur while the power is being restored, please unplug all sensitive equipment, appliances, and electronics until problem is corrected.':
		['PPWD', 'SDXL', 'SDLTSOK'],
	'To prevent damage that may occur while the power is being restored, please unplug all sensitive equipment, appliances, and electronics until problem is corrected.':
		['XCURR', 'LOWVOL', 'HIGHVOL'],
	'To prevent any damage that may occur while the power is being restored, please unplug all sensitive equipment, such as TV’s, refrigerators, computers, all other appliances, until problem is corrected.':
		['PREFLT'],
	'Keep everyone, including pets, away from the hazard.': ['PTFIRE', 'BP', 'HZ', 'ARC'],
	'Avoid standing underneath wires connected to the pole.': ['PTFIRE'],
	'Stay clear of broken poles or anything that is in contact with it. Do not attempt to move the pole or any part of it.':
		['BP'],
	'For Oil Spills - Do not touch the oil or anything that is covered in the oil.': ['HZ'],
	'Stay at least 25 feet away from an arcing wire or anything it may be touching, including water.': ['ARC'],
	'Un-plug sensitive electrical equipment and accessible, major appliances, especially those with motors.': [
		'HOSP',
		'EHS',
		'HIC',
		'PRIMARY',
		'SCHOOL',
	],
	'Do not attempt to move DTE equipment.': ['NONHZD'],
	'To prevent damage that may occur while the power is being restored, please unplug all sensitive equipment, appliances, and electronics until problem is corrected. Do not overload working circuits.':
		['ONELEG'],
	'Do not attempt to remove the tree, limb, or vines yourself.': ['TT'],
	'Do not touch a wire or anything it may be touching, including vines.': ['TT'],
	'Treat all wires as live. Keep yourself, your family, and pets at least 25 feet away from the wire.': ['TT'],
	'Do not attempt to move the wire.': ['UGEXP'],
};

export function PrecautionAdvisements({ callCode }: { callCode?: string }): ReactNode {
	if (!callCode) {
		return null;
	}

	const advisements: ReactNode[] = [];

	for (const [advisement, codes] of Object.entries(precautionAdvisementsMap)) {
		if (codes.includes(callCode)) {
			const key = stableId(advisement);
			advisements.push(<p key={key}>{advisement}</p>);
		}
	}

	if (isEmptyArray(advisements)) {
		return null;
	}

	return <InfoAlert title="Customer Precautions">{advisements}</InfoAlert>;
}

const needsDebrisAdvisementAtom = atom((get) => {
	const answers = get(selectedAnswersAtom);

	// TODO: add logic for other paths than tree trim where there might be debris

	const treeTrim = isTreeTrimIssue(answers);
	if (treeTrim) {
		return true;
	}

	return false;
});
needsDebrisAdvisementAtom.debugLabel = 'needsDebrisAdvisementAtom';

function DebrisAdvisementsSummary(props: AdvertisementsProps): ReactNode {
	const needsAdvisement = useAtomValue(needsDebrisAdvisementAtom);

	if (!needsAdvisement || props.callCode === 'TT') {
		return null;
	}

	return (
		<InfoAlert title="Debris Handling">
			<DebrisAdvisement />
		</InfoAlert>
	);
}

export function AdvisementSummary(): ReactNode {
	// Need to run mapping first to map answers to call code
	const callCode = useProjectedCallCode();

	return (
		<>
			{/* <EmergencyAdvisements callCode={callCode} /> */}
			<FrequentOutageAdvisements callCode={callCode} />
			<WarningAdvisements callCode={callCode} />
			<ExpectationAdvisements callCode={callCode} />
			<DebrisAdvisementsSummary callCode={callCode} />
			<PrecautionAdvisements callCode={callCode} />
			<SelfServicePromotionAdvisement />
		</>
	);
}
